import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'

interface McpConnectExamplesProps {
  /** Raw token during the one-time reveal; examples fall back to a placeholder after that. */
  token: string | null
}

function CodeSnippet({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative">
      <pre className="p-4 rounded-lg bg-muted text-xs overflow-x-auto whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
      <Button size="sm" variant="secondary" className="absolute top-2 right-2" onClick={copy}>
        {copied ? <><Check className="size-4 mr-1" /> Copied</> : <><Copy className="size-4 mr-1" /> Copy</>}
      </Button>
    </div>
  )
}

export function McpConnectExamples({ token }: McpConnectExamplesProps) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.banshee.chat'
  const url = `${origin}/mcp`
  const shellToken = token ?? '<your-api-key>'

  const cliExample = `claude mcp add banshee --transport http ${url} --header "Authorization: Bearer ${shellToken}"`

  const mcpJsonExample = [
    '{',
    '  "mcpServers": {',
    '    "banshee": {',
    '      "type": "http",',
    `      "url": "${url}",`,
    '      "headers": { "Authorization": "Bearer ${BANSHEE_MCP_KEY}" }',
    '    }',
    '  }',
    '}',
  ].join('\n')

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div>
        <h4 className="text-sm font-medium">Connect an agent</h4>
        <p className="text-sm text-muted-foreground">
          {token
            ? 'Examples below include your new key.'
            : 'Replace the placeholder with a key — shown once at creation.'}
        </p>
      </div>

      <div className="space-y-2">
        <Label>Claude Code (CLI)</Label>
        <CodeSnippet code={cliExample} />
      </div>

      <div className="space-y-2">
        <Label>.mcp.json (project config)</Label>
        <CodeSnippet code={mcpJsonExample} />
        <p className="text-sm text-muted-foreground">
          <code className="bg-muted px-1 rounded">.mcp.json</code> is usually committed to your repo, so it references
          the key via the <code className="bg-muted px-1 rounded">BANSHEE_MCP_KEY</code> environment variable —
          export it in your shell ({token ? <code className="bg-muted px-1 rounded break-all">export BANSHEE_MCP_KEY={token}</code> : <code className="bg-muted px-1 rounded">export BANSHEE_MCP_KEY=&lt;your-api-key&gt;</code>}) rather than committing the key itself.
        </p>
      </div>
    </div>
  )
}
